import { normaliseBaseUrl } from "./config.js";

export class ApiError extends Error {
  constructor(message, { status = 0, code = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }

  get isAuthFailure() {
    return this.status === 401 || this.status === 403;
  }

  get isDisabled() {
    return this.status === 503;
  }
}

export const UNREACHABLE =
  "The browser could not read a response from the server. Either it is not running, " +
  "or it refused this origin — add this page's origin to ADMIN_UI_ORIGIN on the bot and restart it.";

function messageForStatus(status, statusText) {
  if (status === 0) return UNREACHABLE;
  if (status === 401) return "Rejected — check the admin token.";
  if (status === 503) return "The admin API is disabled on that server.";
  return `${status} ${statusText}`.trim();
}

export function createHttp(getSession) {
  async function request(path, { method = "GET", body, signal } = {}) {
    const session = getSession();
    const url = `${normaliseBaseUrl(session?.baseUrl)}${path}`;

    const headers = { accept: "application/json" };
    if (body !== undefined) headers["content-type"] = "application/json";
    if (session?.token) headers["x-admin-token"] = session.token;

    const init = { method, headers, signal };
    if (body !== undefined) init.body = JSON.stringify(body);

    let response;
    try {
      response = await fetch(url, init);
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      throw new ApiError(messageForStatus(0), { status: 0 });
    }

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      throw new ApiError(
        payload?.error || messageForStatus(response.status, response.statusText),
        { status: response.status, code: payload?.code ?? null },
      );
    }

    return payload;
  }

  return {
    get: (path, options) => request(path, { ...options, method: "GET" }),
    post: (path, body, options) => request(path, { ...options, method: "POST", body }),
  };
}
